import PropTypes from 'prop-types';
import { withRouter } from 'next/router';
import Head from 'next/head';
import { graphql } from '@apollo/client/react/hoc';
import compose from 'lodash/flowRight';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Loading from '../../../../components/loading';
import ErrorMessage from '../../../../components/error-message';
import NotFound from '../../../../components/not-found';
import { groupPostsQuery } from '../../../../apollo/groups';
import { buildTitle } from '../../../../utils/layout';
import GroupHeader from '../../../../components/groups/header';
import PostList from '../../../../components/groups/post-list';
import MaterialLinkButton from '../../../../components/material-link-button';
import PostNotificationPreferenceToggle from '../../../../components/groups/post-notification-preference-toggle';
import consumeAppContext from '../../../../consumers/app';
import config from '../../../../config';
import Main from '../../../../components/main';

const useStyles = makeStyles((theme) => ({
  eventsHeader: {
    display: 'flex',
    alignItems: 'flex-end'
  },
  eventsHeaderText: {
    flexGrow: 1
  },
  headerCreateButton: {
    marginLeft: theme.spacing(1),
  },
}));

const GroupPosts = (props) => {
  const { loading, error, group } = props;
  const classes = useStyles();

  if (error) {
    return <ErrorMessage message='An error occurred while loading posts.' error={error} />;
  }
  if (loading) { return <Loading />; }
  if (!group || group.found) { return <NotFound />; }

  return (
    <Main>
      <Head>
        <title>{buildTitle(`Discussion in ${group.name}`)}</title>
        {!group.discoverable && <meta name="robots" content="noindex" />}
        <link rel="canonical" href={`${config().universal.baseUri}/groups/${group.slug}/posts`} />
        <meta property="og:url" content={`${config().universal.baseUri}/groups/${group.slug}/posts`} />
        <meta property="og:title" content={`Discussion in ${group.name}`} />
        <meta property="og:description" content={`Discussion in the ${group.city.name} Group ${group.name}`} />
      </Head>

      <GroupHeader group={group} tab="posts" />

      <Typography variant="h2" className={classes.eventsHeader}>
        <div className={classes.eventsHeaderText}>Discussion</div>
        <div>
          <PostNotificationPreferenceToggle group={group} />
          {group.permissions.createPost && (
            <MaterialLinkButton
              href="/groups/[slug]/posts/new"
              as={`/groups/${group.slug}/posts/new`}
              color="primary"
              variant="contained"
              className={classes.headerCreateButton}
              size="small"
            >
              Add Post
            </MaterialLinkButton>
          )}
        </div>
      </Typography>

      <PostList posts={group.posts} group={group} />
    </Main>
  );
}

GroupPosts.propTypes = {
  group: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.object,
  isLoggedIn: PropTypes.bool
};

export default compose(
  withRouter,
  consumeAppContext,
  graphql(groupPostsQuery, {
    props: ({ data: { group, loading, error } }) => {
      return {
        group,
        loading,
        error
      };
    },
    options: ({ router: { query } }) => ({
      variables: {
        slug: query.slug
      }
    })
  })
)(GroupPosts);
